import React, { useCallback } from "react";
import PluginSlotRenderer from "./SlotRenderer";
import PluginSlotWrap from "./PluginSlotWrap";
import usePluginSlot from "./usePluginSlot";
import { runPipeline, anyHookTrue, runHook } from "@markedesk/plugin-sdk/ui";

/**
 * PluginBridge — ponto ÚNICO de integração de plugins na UI.
 *
 * Reúne tudo que o app precisa de plugins:
 *  - <PluginBridge slot=... />        renderiza as ações declaradas no slot
 *  - <PluginBridge slot=...>nativo</> entrega o elemento nativo ao controlador
 *                                     registrado com markedesk.registerSlot()
 *  - usePluginBridge(event).run(...)  intercepta uma ação (pipeline before/on/after)
 *  - usePluginBridge().shouldHide(...) consulta hooks de decisão
 *  - PluginHtmlModalHost              host dos modais HTML (montar 1x no layout)
 *
 * Os componentes do app devem usar SEMPRE este módulo. `PluginSlotRenderer` é o
 * motor de render das ações; `usePluginSlot` é o motor do wrap imperativo.
 *
 * ## Por que os dois motores vivem aqui
 *
 * O declarativo (`provides.actions`, abril/2026) e o imperativo
 * (`registerSlot`, maio/2026) cresceram separados: o primeiro foi aplicado a
 * ~20 pontos, o segundo a 4. Nada no código dizia qual ponto atendia qual, e a
 * consequência era falha MUDA — o plugin registrava um controlador, ninguém o
 * chamava, e não havia erro nem log. Dois casos reais viveram assim: o botão do
 * plugin-tour na navbar e o "Enviar template" do notificamehub no modal de Novo
 * Ticket — este último impedindo iniciar conversa por template fora da janela de
 * 24h desde que o canal foi reescrito.
 *
 * Com os dois motores no mesmo componente, qualquer slot aceita as duas formas:
 * declarar um item, ou assumir o elemento nativo.
 *
 * ## `wrap` e `actions`
 *
 * Ambos ligados por padrão. Existem para o caso — hoje único — em que o MESMO
 * slot é servido por DOIS pontos diferentes da tela: `message-input` envolve a
 * caixa de digitação num lugar e desenha os botões de ação em outro. Ali, cada
 * ponto liga só o motor que lhe cabe; sem isso, o controlador do plugin seria
 * montado duas vezes (dois `onMount`, dois SlotApi) — e um plugin que guarda
 * estado global, como o notificamehub e seu unsubscribe de socket, perderia a
 * referência da primeira montagem.
 *
 * Isso é convenção por ponto de uso, então não basta: quem escrever um segundo
 * ponto para um slot já servido não tem como saber que existe um dono. O SDK
 * cobre os dois lados dessa convenção com aviso em desenvolvimento — wrap
 * montado duas vezes com o mesmo contexto, e slot cujos pontos estão TODOS
 * desligados (controlador registrado que nunca roda).
 *
 * ## Menus não aceitam wrapper
 *
 * `as="menu-items"` renderiza dentro de um `<Menu>` do MUI, que clona os filhos
 * diretos para passar foco e navegação por teclado. Envolvê-los numa `div`
 * quebraria isso, então nesses pontos o wrap não liga — nem por padrão nem se
 * pedido. Ali a extensão é por ação declarada (`provides.actions`).
 */
export default function PluginBridge({
  slot,
  context = {},
  as = "buttons",
  layout = "contents",
  wrap = true,
  actions = true,
  onInsertText,
  onPluginMessage,
  children,
}) {
  const wrapEnabled = wrap && as !== "menu-items";
  // Hooks não podem ficar atrás de early-return — o hook é sempre chamado, e
  // `enabled` diz se ESTE ponto executa controladores. O slot continua sendo
  // anunciado ao SDK mesmo desligado: é assim que o diagnóstico distingue "tela
  // não aberta" de "nenhum ponto executa o controlador".
  const { hasControllers, originalRef, layerRef } = usePluginSlot(slot || "", context, {
    layout,
    enabled: wrapEnabled,
    // O mesmo callback que o motor declarativo usa para "insert-text": quem
    // desenha um seletor pelo registerSlot precisa entregar a escolha no input.
    onInsertText,
  });

  if (!slot) return children ?? null;

  const acoes = actions ? (
    <PluginSlotRenderer
      slot={slot}
      context={context}
      as={as}
      onInsertText={onInsertText}
      onPluginMessage={onPluginMessage}
    />
  ) : null;

  // Sem controlador — a maioria dos pontos, quase sempre — a saída é idêntica à
  // de antes desta unificação: Fragment, nenhuma `div` a mais no DOM, e o
  // contexto sequer é serializado.
  if (!hasControllers) {
    return (
      <>
        {children}
        {acoes}
      </>
    );
  }

  // Com controlador: nativo (sob controle do plugin) → camada imperativa →
  // ações declaradas. As duas formas convivem no mesmo ponto, nessa ordem.
  return (
    <>
      <PluginSlotWrap layout={layout} originalRef={originalRef} layerRef={layerRef}>
        {children}
      </PluginSlotWrap>
      {acoes}
    </>
  );
}

/**
 * Hook de integração de plugins.
 *
 * @param {string} event  evento para interceptação (ex: "create-ticket")
 * @returns {{ run, shouldHide, query }}
 *   run(ctx, coreAction)      → pipeline before → on(coreAction) → after.
 *                               Retorna o resultado, ou undefined se cancelado.
 *   shouldHide(hookName, ctx) → true se algum plugin pedir para esconder algo.
 *   query(hookName, ctx)      → resultados crus dos hooks de decisão.
 */
export function usePluginBridge(event) {
  const run = useCallback(
    (ctx, coreAction) => runPipeline(event, ctx || {}, coreAction),
    [event]
  );
  const shouldHide = useCallback((hookName, ctx) => anyHookTrue(hookName, ctx), []);
  const query = useCallback((hookName, ctx) => runHook(hookName, ctx), []);
  return { run, shouldHide, query };
}

export { default as PluginHtmlModalHost } from "./PluginHtmlModalHost";
